import { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { createPortal } from 'react-dom';

/**
 * ImageLightbox - A reusable component for displaying images with zoom functionality
 * Click on any image to open it in a full-screen lightbox modal
 * Zoom controls: +10% / -10% increments
 * Uses React Portal to render outside of parent container hierarchy
 * Supports: JPG, PNG, WebP, GIF, and SVG files
 */
export default function ImageLightbox({ src, alt = "Image", className = "", containerClassName = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [imageStatus, setImageStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  
  // Check if the source is an SVG file
  const isSvg = src && (src.toLowerCase().endsWith('.svg') || src.includes('.svg?'));

  // Reset status when src changes
  useEffect(() => {
    setImageStatus('loading');
    
    // For SVG files, set status to loaded immediately since onLoad may not fire consistently
    if (isSvg && src) {
      // Give it a small delay to allow the image to start loading
      const timer = setTimeout(() => {
        setImageStatus('loaded');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [src, isSvg]);

  // Handle body scroll lock when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const openLightbox = useCallback(() => {
    // For SVG or loaded images, open the lightbox
    if (imageStatus === 'loaded' || isSvg) {
      setIsOpen(true);
      setScale(1); // Start at 100%
    }
  }, [imageStatus, isSvg]);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
    setScale(1);
  }, []);

  // Zoom in by 10%
  const zoomIn = useCallback((e) => {
    if (e) e.stopPropagation();
    setScale(prev => Math.min(prev + 0.1, 3)); // Max 300%
  }, []);

  // Zoom out by 10%
  const zoomOut = useCallback((e) => {
    if (e) e.stopPropagation();
    setScale(prev => Math.max(prev - 0.1, 0.3)); // Min 30%
  }, []);

  // Reset to 100%
  const resetZoom = useCallback((e) => {
    if (e) e.stopPropagation();
    setScale(1);
  }, []);

  // Fit to screen (150%)
  const fitToScreen = useCallback((e) => {
    if (e) e.stopPropagation();
    setScale(1.5);
  }, []);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === '0') resetZoom();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeLightbox, zoomIn, zoomOut, resetZoom]);

  const handleImageError = () => {
    console.log('ImageLightbox: Image failed to load:', src);
    // Don't set error for SVG since we handle them differently
    if (!isSvg) {
      setImageStatus('error');
    }
  };

  const handleImageLoad = () => {
    setImageStatus('loaded');
  };

  // Don't render anything if image failed to load (except SVG) or no src
  if ((imageStatus === 'error' && !isSvg) || !src) {
    return null;
  }

  // Lightbox Modal Component - rendered via Portal
  const LightboxModal = () => (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/95"
      style={{ zIndex: 99999 }}
      onClick={closeLightbox}
    >
      {/* Close button */}
      <button
        onClick={closeLightbox}
        className="absolute top-4 right-4 z-10 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-all"
        aria-label="Kapat"
      >
        <X className="w-7 h-7 text-white" />
      </button>

      {/* Zoom controls - Bottom center */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-black/80 backdrop-blur-md rounded-2xl px-5 py-4 shadow-2xl border border-white/10">
        {/* Zoom out button */}
        <button
          onClick={zoomOut}
          disabled={scale <= 0.3}
          className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all disabled:opacity-30 flex items-center gap-2"
          aria-label="Küçült"
        >
          <ZoomOut className="w-5 h-5 text-white" />
          <span className="text-white text-sm font-medium">-10%</span>
        </button>
        
        {/* Current zoom percentage */}
        <button
          onClick={resetZoom}
          className="px-5 py-2 bg-purple-600/50 hover:bg-purple-600/70 rounded-lg transition-all min-w-[90px]"
          title="100%'e sıfırla"
        >
          <span className="text-white font-bold text-lg">
            {Math.round(scale * 100)}%
          </span>
        </button>
        
        {/* Zoom in button */}
        <button
          onClick={zoomIn}
          disabled={scale >= 3}
          className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all disabled:opacity-30 flex items-center gap-2"
          aria-label="Büyüt"
        >
          <span className="text-white text-sm font-medium">+10%</span>
          <ZoomIn className="w-5 h-5 text-white" />
        </button>

        {/* Divider */}
        <div className="w-px h-10 bg-white/30 mx-3"></div>

        {/* Fit to screen button */}
        <button
          onClick={fitToScreen}
          className="px-3 py-2 bg-green-600/50 hover:bg-green-600/70 rounded-lg transition-all flex items-center gap-2"
          title="Büyük göster"
        >
          <Maximize2 className="w-5 h-5 text-white" />
          <span className="text-white text-sm font-medium">Büyük</span>
        </button>
      </div>

      {/* Image container - Centered */}
      <div 
        className="flex items-center justify-center w-full h-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="transition-transform duration-200 ease-out shadow-2xl rounded-lg"
          style={{ 
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            cursor: scale < 3 ? 'zoom-in' : 'zoom-out',
            // Add white background for SVG visibility
            backgroundColor: isSvg ? 'white' : 'transparent',
            padding: isSvg ? '16px' : '0'
          }}
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            if (scale < 3) {
              setScale(prev => Math.min(prev + 0.2, 3));
            } else {
              setScale(1);
            }
          }}
        />
      </div>

      {/* Instructions - Top left */}
      <div className="absolute top-4 left-4 text-white/70 text-sm hidden md:block bg-black/60 px-4 py-2 rounded-lg">
        <p>ESC: Kapat | +/-: Yakınlaştır | 0: Sıfırla | Resme tıkla: Büyüt</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Thumbnail Image - Clickable */}
      <div 
        className={`${(imageStatus === 'loaded' || isSvg) ? 'cursor-zoom-in' : ''} ${containerClassName}`} 
        onClick={openLightbox}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`${className} ${imageStatus === 'loading' && !isSvg ? 'opacity-0' : 'opacity-100'}`}
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ 
            display: imageStatus === 'error' && !isSvg ? 'none' : 'block',
            transition: 'opacity 0.3s ease',
            // Add white background for SVG to ensure visibility
            backgroundColor: isSvg ? 'white' : 'transparent',
            padding: isSvg ? '4px' : '0',
            borderRadius: isSvg ? '4px' : '0'
          }}
        />
        {/* Zoom indicator overlay - only show if image loaded successfully */}
        {(imageStatus === 'loaded' || isSvg) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-all duration-200 rounded-lg opacity-0 hover:opacity-100">
            <div className="bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
              <Maximize2 className="w-5 h-5 text-gray-700" />
              <span className="text-gray-700 text-sm font-medium">Tıkla: Büyüt</span>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal - Rendered via Portal to escape parent z-index */}
      {isOpen && (imageStatus === 'loaded' || isSvg) && createPortal(
        <LightboxModal />,
        document.body
      )}
    </>
  );
}
